import { useState, useEffect, useRef } from "react";
import FlagStripe from '@/components/FlagStripe';
import { Link, useLocation } from "react-router-dom";
import { isNativeApp } from "@/lib/platform";
import { useAppReviewFlags } from "@/config/appReviewFlags";
import { Moon, Sun, X } from "lucide-react";
import { useBrand } from "@/config/brands";
import { useTheme } from "@/contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import type { LanguageCode } from "@/i18n";
import { flagUrl } from "@/i18n";
import { trackLanguageChange } from "@/utils/analytics";

// Patriotic design tokens
const T_LIGHT = {
  bg: "#FAF9F6",
  headerBg: "#ffffff",
  navLogo: "#002868",
  text: "#1B1B1B",
  textMuted: "rgba(27,27,27,0.62)",
  gold: "#C5A55A",
  red: "#B22234",
  blue: "#002868",
  border: "rgba(27,27,27,0.12)",
  bannerBg: "#002868",
  bannerText: "#ffffff",
};
const T_DARK = {
  bg: "#0a1628",
  headerBg: "#0a1628",
  navLogo: "#FAF9F6",
  text: "#FAF9F6",
  textMuted: "rgba(250,249,246,0.55)",
  gold: "#C5A55A",
  red: "#B22234",
  blue: "#5B9BD5",
  border: "rgba(250,249,246,0.12)",
  // Dark-mode banner — kept brand-stable navy (matches light-mode bannerBg)
  // with cream text. The earlier inversion (#F7F4EF bg + #002868 text) was
  // intended as a negative-space accent but rendered as dark-navy-on-darker-
  // navy due to parent-bg bleed against the page's #0a1628 base. Brand-stable
  // is more legible.
  bannerBg: "#002868",
  bannerText: "#FAF9F6",
};

const serif = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const sans = "'Instrument Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

export function AhpGlobalHeader() {
  const reviewFlags = useAppReviewFlags();
  const brand = useBrand();
  const { isDark, toggle } = useTheme();
  const { t, i18n } = useTranslation(["homepage", "common"]);
  const { currentLanguage, currentLangInfo, isRTL, changeLanguage, languages } = useLanguage();
  const location = useLocation();
  const T = isDark ? T_DARK : T_LIGHT;

  // Only render for AHP brand
  if (brand.key !== "ahp") return null;

  // Skip header when embedded in an iframe (for in-modal previews)
  if (typeof window !== "undefined" && new URLSearchParams(window.location.search).get("embed") === "1") {
    return null;
  }

  // Announcement banner — never shown in the native app (it reads as a web
  // promo bar; the app's chrome is just the header + flag stripe).
  const [showTopBanner, setShowTopBanner] = useState(() =>
    !isNativeApp() && !sessionStorage.getItem("ahp_top_banner_dismissed")
  );
  const dismissTopBanner = () => {
    sessionStorage.setItem("ahp_top_banner_dismissed", "1");
    setShowTopBanner(false);
  };

  // Header height measurement
  const headerRef = useRef<HTMLElement>(null);
  const [headerHeight, setHeaderHeight] = useState(62);
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    // Observe the BORDER box, not the default content box. The iOS safe-area
    // inset is applied as the header's padding-top (env(safe-area-inset-top)),
    // which grows the border box but NOT the content box. With a content-box
    // observer the callback never fires when iOS resolves the inset late, so the
    // spacer below stays too short and page titles slide up under the header.
    // Border-box observation catches the padding/inset growth too. We also
    // re-measure on orientation/viewport changes for good measure.
    const measure = () => setHeaderHeight(el.offsetHeight);
    const ro = new ResizeObserver(measure);
    ro.observe(el, { box: 'border-box' });
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, []);

  // Language dropdown
  const [langOpen, setLangOpen] = useState(false);
  const langRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const topOffset = showTopBanner ? 40 : 0;
  const FLAG_STRIPE_H = 5;
  const totalHeight = topOffset + headerHeight + FLAG_STRIPE_H;
  // Where the safe-area inset is accounted for differs by mode:
  //  • Web (banner present): the banner carries the inset as paddingTop, so the
  //    fixed header/flag-stripe/spacer below it must add the inset once.
  //  • Native (no banner): the header itself carries the inset in its own
  //    paddingTop, so headerHeight (offsetHeight) already includes it — adding
  //    env() again here would double-count it and open a gap. So: "0px".
  const insetExpr = showTopBanner ? "env(safe-area-inset-top, 0px)" : "0px";

  // Dark mode toggle label
  const dmLabel = isDark ? t("common:darkMode.switchToLight") : t("common:darkMode.switchToDark");

  // Nav links with active highlighting
  const navLinks = [
    { to: "/search", label: t("common:nav.search") },
    { to: "/scenarios", label: t("common:nav.scenarios") },
    { to: "/hospital-rankings", label: t("common:nav.rankings") },
    { to: "/contact", label: t("common:nav.contact", { defaultValue: "Contact" }) },
  ];

  return (
    <>
      {/* ─── ANNOUNCEMENT BANNER ─── */}
      {showTopBanner && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 500,
            height: 40,
            // Fill the iOS status-bar / Dynamic Island area with the banner's
            // navy and push the text below it (env() is 0 on web, so no-op there).
            paddingTop: "env(safe-area-inset-top, 0px)",
            boxSizing: "content-box",
            backgroundColor: T.bannerBg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            paddingLeft: 48,
            paddingRight: 48,
          }}
        >
          <span
            style={{
              fontFamily: sans,
              fontSize: 13,
              color: T.bannerText,
              textAlign: "center",
              fontWeight: isDark ? 600 : 400,
            }}
          >
            ★&nbsp; {t("homepage:banner.text")}&nbsp; ★
          </span>
          <button
            onClick={dismissTopBanner}
            aria-label="Dismiss"
            style={{
              position: "absolute",
              left: isRTL ? "auto" : 39,
              right: isRTL ? 39 : "auto",
              top: "50%",
              transform: "translateY(-50%)",
              background: "none",
              border: "none",
              cursor: "pointer",
              padding: 4,
              color: isDark ? "rgba(0,40,104,0.5)" : "rgba(255,255,255,0.7)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* ─── FIXED HEADER ─── */}
      <header
        ref={headerRef}
        style={{
          position: "fixed",
          // With the banner present (web) the header sits below it; with it gone
          // (native) the header itself owns the top and fills the safe-area /
          // Dynamic Island so the status bar reads on the header background.
          top: showTopBanner ? `calc(${topOffset}px + env(safe-area-inset-top, 0px))` : 0,
          left: 0,
          right: 0,
          zIndex: 400,
          // Frosted/opaque: content scrolling underneath is fogged, not visible
          // through — the iOS-native top-bar aesthetic. Navy in dark, white in light.
          backgroundColor: isDark ? "rgba(10,22,40,0.82)" : "rgba(255,255,255,0.85)",
          backdropFilter: "saturate(180%) blur(20px)",
          WebkitBackdropFilter: "saturate(180%) blur(20px)",
          borderTop: `4px solid ${T.red}`,
          borderBottom: `1px solid ${T.border}`,
          // Native: tuck 12px INTO the OS safe-area (Apple's inset includes
          // breathing room past the status bar/island) and slim our own pad
          // 10→6 — reclaims ~16px of dead top space on device. Floor keeps
          // web (env()=0) and edge devices sane.
          padding: showTopBanner
            ? "10px 0"
            : "calc(6px + max(env(safe-area-inset-top, 0px) - 12px, 0px)) 0 8px 0",
        }}
      >
        <div className="max-w-[1260px] mx-auto px-[10px] sm:px-12 flex items-center gap-x-4">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-3 flex-shrink-0"
            style={{ textDecoration: "none" }}
          >
            <span className="text-2xl" style={{ color: T.gold }}>
              &#9733;
            </span>
            <span
              className="text-xl sm:text-2xl"
              style={{
                fontFamily: serif,
                fontWeight: 400,
                color: T.navLogo,
                letterSpacing: "-0.5px",
                whiteSpace: "nowrap",
              }}
            >
              American Hospital Prices
            </span>
          </Link>

          {/* Beta badge — links to /release-notes. Sits flush against the
              wordmark so it reads as part of the brand. Subtle gold border on
              transparent so it doesn't compete with nav-link weight, but the
              cursor + hover-fill make it obviously clickable.
              Hidden on the native app during App Store review (Apple rejects
              "beta" references) — remote-flag flips it back post-approval. */}
          {reviewFlags.showBeta && (
          <Link
            to="/release-notes"
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold tracking-wide uppercase flex-shrink-0 transition-colors"
            style={{
              border: `1px solid ${T.gold}`,
              color: T.gold,
              textDecoration: 'none',
              lineHeight: 1,
              letterSpacing: '0.05em',
              marginLeft: -4,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = String(T.gold); e.currentTarget.style.color = String(T.navLogo); }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = String(T.gold); }}
            title="What we've shipped + what's coming next"
          >
            Beta
          </Link>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Nav links — desktop only */}
          <nav className="hidden sm:flex items-center gap-5 flex-shrink-0">
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to || location.pathname.startsWith(link.to + "/");
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  style={{
                    fontSize: 14,
                    fontFamily: sans,
                    textDecoration: "none",
                    color: isActive ? T.red : (T.textMuted as string),
                    fontWeight: isActive ? 600 : 400,
                    transition: "color 0.15s",
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.color = T.red)}
                  onMouseLeave={(e) => (e.currentTarget.style.color = isActive ? T.red : (T.textMuted as string))}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Divider */}
          <div
            className="hidden sm:block"
            style={{
              width: 1,
              height: 24,
              backgroundColor: T.border,
              flexShrink: 0,
            }}
          />

          {/* Language selector (flag button) */}
          <div ref={langRef} style={{ position: "relative", flexShrink: 0 }}>
            <button
              onClick={() => setLangOpen((v) => !v)}
              aria-label={`Language: ${currentLangInfo.nativeName}`}
              title={`Language: ${currentLangInfo.nativeName}`}
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                border: "none",
                background: langOpen
                  ? isDark ? "rgba(197,165,90,0.15)" : "rgba(0,40,104,0.08)"
                  : "transparent",
                fontSize: 20,
                lineHeight: 1,
                transition: "background 0.15s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDark
                  ? "rgba(197,165,90,0.15)"
                  : "rgba(0,40,104,0.08)";
              }}
              onMouseLeave={(e) => {
                if (!langOpen) e.currentTarget.style.background = "transparent";
              }}
            >
              <img src={flagUrl(currentLangInfo.countryCode, 40)} alt={currentLangInfo.name} width={22} height={16} style={{ borderRadius: 2 }} />
            </button>

            {/* Language dropdown */}
            {langOpen && (
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  ...(isRTL ? { left: 0 } : { right: 0 }),
                  width: 260,
                  maxHeight: 420,
                  overflowY: "auto",
                  borderRadius: 12,
                  boxShadow: "0 8px 40px rgba(0,0,0,0.18)",
                  backdropFilter: "blur(14px)",
                  zIndex: 600,
                  ...(isDark
                    ? {
                        background: "rgba(10,22,40,0.97)",
                        border: "1px solid rgba(197,165,90,0.3)",
                      }
                    : {
                        background: "rgba(255,255,255,0.98)",
                        border: "1px solid rgba(0,0,0,0.1)",
                      }),
                }}
              >
                <div
                  style={{
                    padding: "12px 16px 8px",
                    fontSize: 10,
                    fontWeight: 700,
                    letterSpacing: "2px",
                    textTransform: "uppercase",
                    color: isDark ? "rgba(197,165,90,0.7)" : "rgba(0,40,104,0.5)",
                    borderBottom: isDark
                      ? "1px solid rgba(255,255,255,0.06)"
                      : "1px solid rgba(0,0,0,0.06)",
                  }}
                >
                  Language
                </div>
                {languages.map((lang) => {
                  const isActive = currentLanguage === lang.code;
                  return (
                    <button
                      key={lang.code}
                      onClick={() => {
                        changeLanguage(lang.code as LanguageCode);
                        trackLanguageChange(lang.code);
                        setLangOpen(false);
                      }}
                      style={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 16px",
                        border: "none",
                        background: isActive
                          ? isDark
                            ? "rgba(197,165,90,0.12)"
                            : "rgba(0,40,104,0.06)"
                          : "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "background 0.15s",
                        borderLeft: isActive
                          ? `3px solid ${isDark ? "#C5A55A" : "#002868"}`
                          : "3px solid transparent",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive)
                          e.currentTarget.style.background = isDark
                            ? "rgba(255,255,255,0.04)"
                            : "rgba(0,0,0,0.03)";
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive) e.currentTarget.style.background = "transparent";
                      }}
                    >
                      <img src={flagUrl(lang.countryCode, 40)} alt={lang.name} width={22} height={16} style={{ borderRadius: 2, flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: isActive ? 600 : 400,
                            color: isDark
                              ? isActive
                                ? "#C5A55A"
                                : "rgba(250,249,246,0.85)"
                              : isActive
                                ? "#002868"
                                : "#1B1B1B",
                          }}
                        >
                          {lang.nativeName}
                        </div>
                        <div
                          style={{
                            fontSize: 11,
                            color: isDark
                              ? "rgba(250,249,246,0.35)"
                              : "rgba(27,27,27,0.4)",
                          }}
                        >
                          {lang.name}
                        </div>
                      </div>
                      {isActive && (
                        <span
                          style={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            background: isDark ? "#C5A55A" : "#002868",
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Dark mode toggle */}
          <button
            onClick={toggle}
            aria-label={dmLabel}
            title={dmLabel}
            style={{
              width: 36,
              height: 36,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "none",
              background: "transparent",
              color: isDark ? T.gold : T.blue,
              transition: "background 0.15s",
              flexShrink: 0,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = isDark
                ? "rgba(197,165,90,0.15)"
                : "rgba(0,40,104,0.08)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "transparent";
            }}
          >
            {isDark ? <Sun size={18} /> : <Moon size={18} />}
          </button>
        </div>
      </header>

      {/* Patriotic flag stripe — pinned just below the fixed header; ripples
          on every navigation (PACE-style page-fetch cue). */}
      <FlagStripe
        height={FLAG_STRIPE_H}
        style={{
          position: "fixed",
          top: `calc(${topOffset + headerHeight}px + ${insetExpr})`,
          left: 0,
          right: 0,
          zIndex: 400,
          pointerEvents: "none",
        }}
      />

      {/* Spacer — pushes page content below the fixed header + flag stripe
          (+ the iOS safe-area inset the fixed bars now sit under). */}
      <div style={{ height: `calc(${totalHeight}px + ${insetExpr})` }} />
    </>
  );
}

/**
 * Hook for other components to know the current header offset
 * (so they can position fixed elements below the header)
 */
export function useAhpHeaderOffset(): number {
  const [offset, setOffset] = useState(0);
  useEffect(() => {
    const headerEl = document.querySelector("header");
    const measure = () => {
      // Mirror the EXACT showTopBanner condition (see line ~70): the banner is
      // never rendered on native and disappears once dismissed on web. The old
      // check only looked at the dismissed flag, so on native — where the banner
      // is never shown and thus never "dismissed" — it reserved a phantom 40px
      // band, opening a gap above the sticky search bar.
      const banner = (!isNativeApp() && !sessionStorage.getItem("ahp_top_banner_dismissed")) ? 40 : 0;
      // Header is ~62px, but measure from DOM if possible. offsetHeight includes
      // the iOS safe-area inset padding once env() resolves.
      const headerH = headerEl ? headerEl.offsetHeight : 62;
      // 5px patriotic flag stripe sits just below the header
      const flagStripe = 5;
      setOffset(banner + headerH + flagStripe);
    };
    measure();
    // Border-box observation so the late-resolving safe-area inset (applied as
    // header padding-top) is picked up — a content-box observer would miss it.
    let ro: ResizeObserver | undefined;
    if (headerEl && typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(measure);
      ro.observe(headerEl, { box: "border-box" });
    }
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, []);
  return offset;
}
